import { Box } from "@mui/material"
import type { CellContext, ColumnDef } from "@tanstack/react-table"
import type { ReactElement } from "react"
import React from "react"

import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import type { Location } from "@/graphql/codegen/graphql"
import { CompanyStatus, LocationSortField } from "@/graphql/codegen/graphql"
import { createStatusMap } from "@/utils/statusMap"

export const companyStatusMap = createStatusMap<
  CompanyStatus,
  "primary" | "secondary" | "default" | "error" | "info" | "success" | "warning"
>([
  [CompanyStatus.Active, "success"],
  [CompanyStatus.Pending, "warning"],
  [CompanyStatus.Rejected, "error"],
  [CompanyStatus.Inactive, "secondary"],
])

export type SortableAccessorKey = "company_name" | "name"

export const accessorKeyToLocationSortField = new Map<SortableAccessorKey, LocationSortField>([
  ["company_name", LocationSortField.CompanyName],
  ["name", LocationSortField.LocationName],
])

export const locationSortFieldToAccessoryKey = new Map<LocationSortField, SortableAccessorKey>([
  [LocationSortField.CompanyName, "company_name"],
  [LocationSortField.LocationName, "name"],
])

export const columns: ColumnDef<Location>[] = [
  {
    accessorKey: "company.name",
    header: "Company",
    sortingFn: "alphanumeric",
    cell: ({ row }: CellContext<Location, unknown>): ReactElement => {
      const company = row.original?.company

      return (
        <Box className='ml-3 flex items-center justify-start gap-2 px-1'>
          <StatusIndicator status={company?.status ?? CompanyStatus.Inactive} map={companyStatusMap} />
          <div>{company?.name}</div>
        </Box>
      )
    },
    size: 400,
    enableSorting: true,
  },
  {
    accessorKey: "name",
    header: "Location Name",
    cell: ({ row }: CellContext<Location, unknown>): ReactElement => {
      const { name, isActive } = row.original
      return (
        <Box className='ml-3 flex items-center justify-start gap-2 px-1'>
          <StatusIndicator status={isActive ? CompanyStatus.Active : CompanyStatus.Inactive} map={companyStatusMap} />
          <div>{name}</div>
        </Box>
      )
    },
    sortingFn: "alphanumeric",
    size: 400,
  },
  {
    accessorKey: "",
    header: "Location Address",
    size: 400,
    cell: ({ row }: CellContext<Location, unknown>): ReactElement => {
      const location = row.original
      const addressParts = [
        location.address1,
        location.address2,
        location.city,
        location.region?.name,
        location.country?.name,
      ].filter(Boolean)

      return <>{addressParts.join(", ")}</>
    },
  },
]
